import * as React from 'react';
import { Dialog, Portal } from '@chakra-ui/react';

/**
 * Project Modal.
 *
 * NB-shaped facade over Chakra v3's `Dialog` compositional API.
 * Consumers keep using `<Modal isOpen onClose>` + `Modal.Content` /
 * `Modal.CloseButton` / `Modal.Header` / `Modal.Body` / `Modal.Footer`.
 * Bridges:
 *   - NB `Modal.Content` -> Chakra `Backdrop` + `Positioner` + `Content`
 *     (the facade renders the first two).
 *   - NB `isOpen` / `onClose` -> Chakra `open` / `onOpenChange({open})`.
 */

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'cover' | 'full';
  children: React.ReactNode;
  centered?: boolean;
  overlayColor?: string;
};

const ModalRoot = ({
  isOpen,
  onClose,
  size = 'md',
  children,
  centered,
  overlayColor,
}: ModalProps) => (
  <Dialog.Root
    open={isOpen}
    onOpenChange={(e) => {
      if (!e.open) onClose();
    }}
    size={size}
  >
    <Portal>
      <Dialog.Backdrop bg={overlayColor} />
      <Dialog.Positioner
        {...(centered && {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        })}
      >
        {children}
      </Dialog.Positioner>
    </Portal>
  </Dialog.Root>
);

/**
 * Design system modal surface (Figma "Modal-Style-3"): a Feather sheet with a
 * 20px radius, and a Recoleta title.
 *
 * Only the surface and title type are set here. The design's 351px width and
 * 24px padding belong to the "Is Ayble right for me?" modal composition
 * (ENG-8), not to every modal, and forcing them would squash the long
 * release-of-information copy in `FullBenefitsModal`. Both stay overridable.
 */
const ModalContent = ({
  children,
  ...rest
}: React.ComponentProps<typeof Dialog.Content>) => (
  <Dialog.Content bg="feather.100" rounded="panel" {...rest}>
    {children}
  </Dialog.Content>
);

const ModalHeader = ({
  children,
  ...rest
}: React.ComponentProps<typeof Dialog.Header>) => (
  <Dialog.Header
    color="secondary.500"
    fontFamily="heading"
    fontSize="24px"
    fontWeight="400"
    letterSpacing="-0.02em"
    lineHeight="1.2"
    {...rest}
  >
    {children}
  </Dialog.Header>
);

export const Modal = Object.assign(ModalRoot, {
  Content: ModalContent,
  CloseButton: Dialog.CloseTrigger,
  Header: ModalHeader,
  Body: Dialog.Body,
  Footer: Dialog.Footer,
});
